import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { previewContent } from '@/preview/mergePreviewPages';
import { withDeletedFiles } from '@/gallery/resolve';

/**
 * App Context - Modern React Context API
 * Replaces legacy childContextTypes/contextTypes pattern
 */

const AppContext = createContext({
    setStatus: () => {},
    urlBase: '',
    url: '',
    content: null,
    theme: null,
    toggleMenu: () => {},
    showMenu: false,
});

/**
 * App Context Provider
 * Wraps the app and provides context values to all children
 */
export function AppProvider({ children, value }) {
    const [showMenu, setShowMenu] = useState(false);
    // Appearance preview only. Null for real visitors, so they keep the
    // server-rendered theme. The preview iframe replaces it as the editor
    // sends the unsaved draft.
    const [previewTheme, setPreviewTheme] = useState(null);
    // Unsaved page drafts the Appearance iframe is editing. Keyed by page id
    // so title/nav changes on Work still show while Series is open. A frame
    // showing a version starts with the version's pages from the server.
    const [previewPages, setPreviewPages] = useState(() => value.content?.previewPages || {});

    const toggleMenu = useCallback((v) => {
        setShowMenu(prev => v === undefined ? !prev : !!v);
    }, []);

    /** Upsert or clear one page draft. Pass page: null to drop that id. */
    const setPreviewPage = useCallback((entry) => {
        if (!entry?.id) return;
        setPreviewPages((prev) => {
            if (!entry.page) {
                if (!prev[entry.id]) return prev;
                const next = { ...prev };
                delete next[entry.id];
                return next;
            }
            return { ...prev, [entry.id]: entry.page };
        });
    }, []);

    /** Replace the whole draft map (used when the editor pushes every dirty page). */
    const replacePreviewPages = useCallback((pages) => {
        setPreviewPages(pages && typeof pages === 'object' ? pages : {});
    }, []);

    // Captions the editor saved after this frame loaded, by file id.
    const [previewCaptions, setPreviewCaptions] = useState({});
    const patchPreviewCaptions = useCallback((captions) => {
        if (!captions || typeof captions !== 'object') return;
        setPreviewCaptions((prev) => ({ ...prev, ...captions }));
    }, []);

    // Files the editor uploaded after this frame loaded, so their tiles can
    // show without a reload.
    const [previewFiles, setPreviewFiles] = useState({});
    const addPreviewFiles = useCallback((files) => {
        if (!files || typeof files !== 'object') return;
        setPreviewFiles((prev) => ({ ...prev, ...files }));
    }, []);

    const content = useMemo(() => {
        const base = value.content && Object.keys(previewFiles).length
            ? { ...value.content, files: { ...(value.content.files || {}), ...previewFiles } }
            : value.content;
        const merged = withDeletedFiles(previewContent(base, previewPages));
        const ids = Object.keys(previewCaptions).filter((id) => merged?.files?.[id]);
        if (!ids.length) return merged;
        const files = { ...merged.files };
        ids.forEach((id) => { files[id] = { ...files[id], description: previewCaptions[id] }; });
        return { ...merged, files };
    }, [value.content, previewPages, previewCaptions, previewFiles]);

    const contextValue = {
        ...value,
        content,
        theme: previewTheme || value.theme,
        setPreviewTheme,
        setPreviewPage,
        setPreviewPages: replacePreviewPages,
        patchPreviewCaptions,
        addPreviewFiles,
        toggleMenu,
        showMenu,
    };
    
    return (
        <AppContext.Provider value={contextValue}>
            {children}
        </AppContext.Provider>
    );
}

/**
 * Hook to access app context
 * Use this instead of static contextTypes
 */
export function useAppContext() {
    const context = useContext(AppContext);
    if (context === undefined) {
        throw new Error('useAppContext must be used within an AppProvider');
    }
    return context;
}

/**
 * HOC for class components that need context
 * Use this for class components that can't use hooks
 */
export function withAppContext(Component) {
    return function WrappedComponent(props) {
        const context = useAppContext();
        return <Component {...props} appContext={context} />;
    };
}

export default AppContext;
